import { MEDIA_URL } from "@/config/env";

/**
 * Canonical media URL helper. ALL media files (images, documents, avatars,
 * logos, banners, attachments) must be resolved through this utility so that
 * the base URL stays driven by VITE_MEDIA_BASE_URL across local, staging, and
 * production. Never concatenate paths against `MEDIA_URL` or
 * `import.meta.env.VITE_MEDIA_BASE_URL` directly in features or components.
 *
 * Behavior:
 *  - `null` / `undefined` / `""` → `null` (lets callers branch on a falsy value)
 *  - already-absolute URLs are re-anchored to the configured `MEDIA_URL`
 *    (so a stale host stored on the backend doesn't override the deployed env)
 *  - data: / blob: URIs pass through untouched (local previews, file readers)
 *  - relative paths are joined to `MEDIA_URL` with exactly one slash
 */
export function mediaUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith("data:") || path.startsWith("blob:")) return path;
  if (/^https?:\/\//i.test(path)) {
    try {
      const url = new URL(path);
      return `${MEDIA_URL}${url.pathname}${url.search}${url.hash}`;
    } catch {
      return path;
    }
  }
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${MEDIA_URL}${suffix}`;
}

/** Resolve a list of media paths, dropping any falsy entries. */
export function mediaUrls(paths: ReadonlyArray<string | null | undefined>): string[] {
  return paths
    .map((p) => mediaUrl(p))
    .filter((u): u is string => Boolean(u));
}

/**
 * Resolve a media path with a guaranteed string fallback. Useful for `<img src>`
 * where you want a placeholder instead of an empty attribute.
 */
export function mediaUrlOr(path: string | null | undefined, fallback: string): string {
  return mediaUrl(path) ?? fallback;
}
